/**
 * CommunityMembershipTab — DEV-1414 (SCOPING_COMMUNITY_MEMBERSHIP.md §8.7).
 *
 * The org community's membership OPERATIONS surface: roster + status
 * ladder, the request→approval queue, and the invitation door. Lives as
 * an org-only sub-tab on Community Management, beside Team — belonging
 * vs roles reads in the nav itself.
 *
 * Ratified constraints this component encodes (§8.7, approved mockup
 * mockups/org_membership_tab.html):
 *   - AUDIENCE LAYER ONLY. No role controls anywhere; the tray points to
 *     the Team tab (POLICY_ROLES_AND_TEAMS six-term lock).
 *   - ONE WORKFLOW SOURCE. The queue renders the same workflow_requests
 *     rows the Action Center inbox shows; approve/decline here runs the
 *     identical engine + side-effects path — two views, one state.
 *   - CAPABILITY-GATED. Parent gates rendering on isAdmin (Administrator
 *     grant; a bundle-defined Membership Coordinator rides the same
 *     capability per §8.4 ruling 4).
 *   - CONFIG VS OPERATIONS SPLIT. Configuration (visibility, join policy)
 *     stays in Community Settings → Audience & Permissions; this tab owns
 *     roster/queue/invites — orgs have exactly one roster surface.
 *   - Roster preload is visible-but-disabled until DEV-926 (minors' PII).
 *   - GEO COMMUNITIES NEVER RENDER THIS (self-join; deliberate divergence).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useApiSecurityData } from '../../api/useApiSecurityData';
import {
  deactivateCommunityMembership,
  upsertCommunityMembership,
} from '../../api/security';
import {
  addOrUpdateWorkflow,
  approveRequest,
  rejectRequest,
  cancelRequest,
  getAllWorkflows,
  subscribeWorkflows,
  applyApprovedWorkflowSideEffects,
  applyRejectedWorkflowSideEffects,
  type WorkflowRequest,
} from '../../workflow';
import { sendMembershipInvitation } from '../../Data/Membership/membershipAdmission';
import {
  createOrgInvitation,
  fetchCommunityInvitations,
  reissueInvitation,
  revokeInvitation,
  type SentInvitation,
} from '../../api/invitations';
import styles from './CommunityMembershipTab.module.css';

export interface CommunityMembershipTabProps {
  communityGuid: string;
  communityName: string;
  currentUserGuid: string;
  /** Approve-membership capability (Administrator / Membership Coordinator). */
  isAdmin: boolean;
}

type StatusFilter = 'all' | 'active' | 'invited' | 'suspended' | 'pending';

interface RosterRow {
  membershipId?: string;
  userGuid: string;
  name: string;
  membershipType: string;
  status: string;
  startedAt?: string;
}

const CommunityMembershipTab: React.FC<CommunityMembershipTabProps> = ({
  communityGuid,
  communityName,
  currentUserGuid,
  isAdmin,
}) => {
  const { t } = useAppTranslation('community');
  const navigate = useNavigate();
  const { securityUsers, communityMemberships, refetch } = useApiSecurityData();

  const [workflows, setWorkflows] = useState<WorkflowRequest[]>(() => getAllWorkflows());
  useEffect(() => subscribeWorkflows(() => setWorkflows(getAllWorkflows())), []);

  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<RosterRow | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteMessage, setInviteMessage] = useState('');
  // Option A (single-entry, auto-detect existing vs new — SCOPING_ONBOARDING_FLOW
  // §7 decision 2026-07-06). Coordinator types an email; the box detects whether
  // it belongs to an existing L4E account (client-side, over the hydrated user
  // list) and routes: existing → in-app membership invitation; new → tokenized
  // link. The result panel labels which happened.
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteResult, setInviteResult] = useState<
    { kind: 'existing' | 'new'; email: string; link?: string } | null
  >(null);
  const [linkCopied, setLinkCopied] = useState(false);
  // Sent tokenized invitations (new-person invites) — separate store from
  // membership rows; the raw link is never returned, only reissue can mint a
  // fresh one (Option A display-layer unification + secure-reissue decision).
  const [sentInvites, setSentInvites] = useState<SentInvitation[]>([]);

  const cg = String(communityGuid || '').toUpperCase();
  const me = String(currentUserGuid || '');

  const nameByGuid = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of securityUsers || []) {
      const g = String((u as any).user_guid || '').toUpperCase();
      if (g) m.set(g, String((u as any).display_name || (u as any).name || g.slice(0, 8)));
    }
    return m;
  }, [securityUsers]);

  // DEV-1414 identity verification: approvers confirm requesters by email +
  // profile — resolved LIVE from security users, never stored in the
  // workflow payload (no PII in immutable audit rows).
  const emailByGuid = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of securityUsers || []) {
      const g = String((u as any).user_guid || '').toUpperCase();
      const e = String((u as any).email || '').trim();
      if (g && e) m.set(g, e);
    }
    return m;
  }, [securityUsers]);

  const openProfile = useCallback(
    (guid: string) => navigate(`/profile/${guid}`),
    [navigate],
  );

  // ── Queue: the SAME workflow_requests rows the Action Center shows ──
  const queue = useMemo(
    () =>
      (workflows || []).filter(
        (w) =>
          w.action_type === 'community.membership_request' &&
          String(w.target_guid || '').toUpperCase() === cg &&
          w.status === 'submitted',
      ),
    [workflows, cg],
  );

  // ── Roster: membership rows for this community (single source) ──
  const roster = useMemo<RosterRow[]>(() => {
    const rows: RosterRow[] = [];
    for (const m of communityMemberships || []) {
      if (String((m as any).community_entity_guid || '').toUpperCase() !== cg) continue;
      const status = String((m as any).status || 'active').toLowerCase();
      if (status === 'inactive') continue; // departed — history only
      // Members = people who are actually members. 'pending' (join request)
      // lives in the Requests queue; 'invited' (existing-user invitation)
      // belongs with Sent invitations — neither is a member yet, so keep them
      // out of the roster to avoid duplicating those surfaces. (Full
      // unification of existing-user + new-person invitations into one Sent
      // view is the deferred cleanup.)
      if (status === 'pending' || status === 'invited') continue;
      const userGuid = String((m as any).user_guid || '');
      rows.push({
        membershipId: (m as any).id ? String((m as any).id) : undefined,
        userGuid,
        name: nameByGuid.get(userGuid.toUpperCase()) || userGuid.slice(0, 8),
        membershipType: String((m as any).membership_type || 'org_member'),
        status,
        startedAt: (m as any).started_at ? String((m as any).started_at) : undefined,
      });
    }
    rows.sort((a, b) => a.name.localeCompare(b.name));
    return rows;
  }, [communityMemberships, cg, nameByGuid]);

  const visibleRoster = useMemo(() => {
    const q = search.trim().toLowerCase();
    return roster.filter((r) => {
      if (filter !== 'all' && r.status !== filter) return false;
      if (q && !r.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [roster, filter, search]);

  // Members (any non-inactive row) — excluded from the invite picker.
  const memberGuidSet = useMemo(() => {
    const s = new Set<string>();
    for (const r of roster) s.add(r.userGuid.toUpperCase());
    return s;
  }, [roster]);

  // Sent tokenized invitations (new-person invites) for this org. Loaded
  // once per org and refreshed after generate/reissue. Defined before the
  // invite handlers so they can depend on it.
  const loadSentInvites = useCallback(async () => {
    if (!isAdmin || !communityGuid) return;
    try {
      setSentInvites(await fetchCommunityInvitations(communityGuid));
    } catch { /* non-fatal — the list just stays empty */ }
  }, [isAdmin, communityGuid]);

  useEffect(() => { loadSentInvites(); }, [loadSentInvites]);

  const handleReissue = useCallback(async (invitationGuid: string) => {
    if (busy) return;
    setBusy(true);
    setLinkCopied(false);
    try {
      const res = await reissueInvitation(invitationGuid);
      const full = `${window.location.origin}${res.inviteLink}`;
      setInviteResult({ kind: 'new', email: res.inviteeEmail || '', link: full });
      setSelected(null);
      setInviteOpen(true);
      loadSentInvites();
    } catch (err: any) {
      window.alert(err?.message || t('membershipTab.error'));
    } finally {
      setBusy(false);
    }
  }, [busy, loadSentInvites, t]);

  const handleRevoke = useCallback(async (invitationGuid: string) => {
    if (busy) return;
    if (!window.confirm(t('membershipTab.sent.revokeConfirm'))) return;
    setBusy(true);
    try {
      await revokeInvitation(invitationGuid);
      loadSentInvites();
    } catch (err: any) {
      window.alert(err?.message || t('membershipTab.error'));
    } finally {
      setBusy(false);
    }
  }, [busy, loadSentInvites, t]);

  // Option A detection: does the typed email belong to an existing L4E
  // account? Resolved client-side over the hydrated user list (no email
  // oracle endpoint needed — the coordinator already has the roster loaded).
  const matchedExistingUser = useMemo(() => {
    const e = inviteEmail.trim().toLowerCase();
    if (!e) return null;
    return (securityUsers || []).find(
      (u: any) => String(u.email || '').trim().toLowerCase() === e,
    ) || null;
  }, [securityUsers, inviteEmail]);

  // ── Queue actions — identical engine path to the Action Center ──
  const resolveRequest = useCallback(
    (w: WorkflowRequest, approved: boolean) => {
      if (busy) return;
      setBusy(true);
      try {
        const next = approved ? approveRequest(w, me) : rejectRequest(w, me);
        const applied = approved
          ? applyApprovedWorkflowSideEffects(next, me)
          : applyRejectedWorkflowSideEffects(next, me);
        if (approved && !applied.ok) {
          window.alert(applied.error || t('membershipTab.error'));
          return;
        }
        addOrUpdateWorkflow(next);
        refetch();
      } finally {
        setBusy(false);
      }
    },
    [busy, me, t, refetch],
  );

  // ── Roster actions (audience layer only — no role controls) ──
  const setStatus = useCallback(
    async (row: RosterRow, status: 'active' | 'suspended') => {
      if (busy) return;
      setBusy(true);
      try {
        await upsertCommunityMembership({
          userGuid: row.userGuid,
          communityEntityGuid: communityGuid,
          membershipType: row.membershipType,
          status,
        });
        refetch();
        setSelected(null);
      } catch (err: any) {
        window.alert(err?.message || t('membershipTab.error'));
      } finally {
        setBusy(false);
      }
    },
    [busy, communityGuid, refetch, t],
  );

  const removeMember = useCallback(
    async (row: RosterRow) => {
      if (busy || !row.membershipId) return;
      if (!window.confirm(t('membershipTab.confirmRemove', { name: row.name }))) return;
      setBusy(true);
      try {
        await deactivateCommunityMembership(row.membershipId);
        refetch();
        setSelected(null);
      } catch (err: any) {
        window.alert(err?.message || t('membershipTab.error'));
      } finally {
        setBusy(false);
      }
    },
    [busy, refetch, t],
  );

  /** Withdraw a not-yet-accepted invitation: cancel its workflow + retire the row. */
  const withdrawInvitation = useCallback(
    async (row: RosterRow) => {
      if (busy) return;
      setBusy(true);
      try {
        const w = (workflows || []).find(
          (x) =>
            x.action_type === 'community.membership_invitation' &&
            String(x.target_guid || '').toUpperCase() === cg &&
            String((x.payload as any)?.user_guid || '').toUpperCase() ===
              row.userGuid.toUpperCase() &&
            x.status === 'submitted',
        );
        if (w) addOrUpdateWorkflow(cancelRequest(w, me));
        if (row.membershipId) await deactivateCommunityMembership(row.membershipId);
        refetch();
        setSelected(null);
      } catch (err: any) {
        window.alert(err?.message || t('membershipTab.error'));
      } finally {
        setBusy(false);
      }
    },
    [busy, workflows, cg, me, refetch, t],
  );

  // Option A — one "Generate invite" action that detects and routes.
  const handleGenerateInvite = useCallback(async () => {
    const email = inviteEmail.trim();
    if (busy || !email) return;
    setBusy(true);
    setLinkCopied(false);
    try {
      if (matchedExistingUser) {
        // Existing L4E account → in-app membership invitation (workflow).
        const guid = String(
          (matchedExistingUser as any).user_guid || (matchedExistingUser as any).userGuid || '',
        );
        if (memberGuidSet.has(guid.toUpperCase())) {
          window.alert(t('membershipTab.invite.alreadyMember'));
          setBusy(false);
          return;
        }
        await sendMembershipInvitation({
          inviterGuid: me,
          inviterName: nameByGuid.get(me.toUpperCase()) || '',
          inviteeGuid: guid,
          inviteeName: nameByGuid.get(guid.toUpperCase()) || inviteName.trim(),
          communityGuid,
          communityName,
          message: inviteMessage,
        });
        setInviteResult({ kind: 'existing', email });
      } else {
        // New person → tokenized invitation + copyable link (§7.2).
        const res = await createOrgInvitation({
          inviteeEmail: email,
          inviteeName: inviteName.trim() || undefined,
          communityGuid,
          personalMessage: inviteMessage.trim() || undefined,
        });
        const full = `${window.location.origin}${res.inviteLink}`;
        setInviteResult({ kind: 'new', email, link: full });
        loadSentInvites();
      }
      refetch();
    } catch (err: any) {
      // On failure (e.g. a duplicate pending invite), refresh the Sent list
      // so the existing invitation — and its Reissue button — is right there.
      loadSentInvites();
      window.alert(err?.message || t('membershipTab.error'));
    } finally {
      setBusy(false);
    }
  }, [
    busy, inviteEmail, matchedExistingUser, memberGuidSet, me, nameByGuid,
    inviteName, communityGuid, communityName, inviteMessage, refetch, t,
    loadSentInvites,
  ]);

  // Reset the composer when the tray closes.
  const closeInvite = useCallback(() => {
    setInviteOpen(false);
    setInviteEmail('');
    setInviteName('');
    setInviteMessage('');
    setInviteResult(null);
    setLinkCopied(false);
  }, []);

  const statusLabel = (s: string) =>
    t(`membershipTab.status.${s}` as any) || s;

  const openTrayFor = (row: RosterRow) => {
    setInviteOpen(false);
    setSelected(row);
  };

  // 'pending' and 'invited' dropped — those people aren't members yet and
  // live in the Requests queue / Sent invitations, not the roster.
  const filters: StatusFilter[] = ['all', 'active', 'suspended'];

  return (
    <div className={styles.shell}>
      <div className={styles.content}>
        {/* Doors */}
        <div className={styles.doors}>
          <button
            type="button"
            className={styles.primaryBtn}
            disabled={!isAdmin || busy}
            onClick={() => {
              setSelected(null);
              setInviteResult(null);
              setLinkCopied(false);
              setInviteOpen(true);
            }}
          >
            {t('membershipTab.inviteMember')}
          </button>
          <button
            type="button"
            className={styles.btn}
            disabled
            title={t('membershipTab.preloadDeferred')}
          >
            {t('membershipTab.preloadRoster')}
          </button>
          <span className={styles.doorNote}>{t('membershipTab.preloadDeferred')}</span>
        </div>

        {/* Requests queue — one workflow source, two views (§8.7) */}
        <section>
          <h3 className={styles.sectionHeading}>
            {t('membershipTab.requestsHeading')}
            <span className={styles.count}> ({queue.length})</span>
          </h3>
          {queue.length === 0 ? (
            <div className={styles.queueEmpty}>{t('membershipTab.queueEmpty')}</div>
          ) : (
            <div className={styles.queue}>
              {queue.map((w) => {
                const p: any = w.payload || {};
                const requesterGuid = String(p.user_guid || '');
                const who =
                  nameByGuid.get(requesterGuid.toUpperCase()) ||
                  String(p.user_display_name || '');
                const email = emailByGuid.get(requesterGuid.toUpperCase()) || '';
                return (
                  <div key={w.workflow_guid} className={styles.request}>
                    <div className={styles.requestWho}>
                      <div className={styles.requestName}>
                        <button
                          type="button"
                          className={styles.profileLink}
                          title={t('membershipTab.tray.viewProfile')}
                          onClick={() => openProfile(requesterGuid)}
                        >
                          {who}
                        </button>
                        {email ? <span className={styles.requestEmail}> · {email}</span> : null}
                      </div>
                      {p.custom_message ? (
                        <div className={styles.requestMsg}>“{p.custom_message}”</div>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className={styles.approveBtn}
                      disabled={!isAdmin || busy}
                      onClick={() => resolveRequest(w, true)}
                    >
                      {t('membershipTab.approve')}
                    </button>
                    <button
                      type="button"
                      className={styles.declineBtn}
                      disabled={!isAdmin || busy}
                      onClick={() => resolveRequest(w, false)}
                    >
                      {t('membershipTab.decline')}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <div className={styles.queueSource}>{t('membershipTab.queueSource')}</div>
        </section>

        {/* Sent invitations — tokenized new-person invites (Slice C4).
            Separate store from membership rows; raw link never stored, so
            reissue mints a fresh one. */}
        {isAdmin && sentInvites.length > 0 && (
          <section>
            <h4 className={styles.sectionHeading}>{t('membershipTab.sent.title')}</h4>
            <div className={styles.sentList}>
              {sentInvites.map((inv) => {
                const pending = inv.status === 'pending';
                return (
                  <div key={inv.invitationGuid} className={styles.sentRow}>
                    <div className={styles.sentMain}>
                      <span className={styles.sentEmail}>{inv.inviteeEmail}</span>
                      {inv.inviteeName && (
                        <span className={styles.sentName}>{inv.inviteeName}</span>
                      )}
                    </div>
                    <span className={`${styles.sentStatus} ${styles[`status_${inv.status}`] || ''}`}>
                      {t(`membershipTab.status.${inv.status}` as any) || inv.status}
                    </span>
                    {pending && (
                      <>
                        <button
                          type="button"
                          className={styles.btn}
                          disabled={busy}
                          onClick={() => handleReissue(inv.invitationGuid)}
                          title={t('membershipTab.sent.reissueHint')}
                        >
                          {t('membershipTab.sent.reissue')}
                        </button>
                        <button
                          type="button"
                          className={styles.btn}
                          disabled={busy}
                          onClick={() => handleRevoke(inv.invitationGuid)}
                          title={t('membershipTab.sent.revokeHint')}
                        >
                          {t('membershipTab.sent.revoke')}
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            <div className={styles.trayNote}>{t('membershipTab.sent.note')}</div>
          </section>
        )}

        {/* Roster */}
        <section>
          <h3 className={styles.sectionHeading}>
            {t('membershipTab.membersHeading')}
            <span className={styles.count}> ({visibleRoster.length})</span>
          </h3>
          <div className={styles.tools}>
            <input
              type="text"
              className={styles.search}
              placeholder={t('membershipTab.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {filters.map((f) => (
              <button
                key={f}
                type="button"
                className={filter === f ? styles.chipOn : styles.chip}
                onClick={() => setFilter(f)}
              >
                {t(`membershipTab.filter.${f}` as any)}
              </button>
            ))}
          </div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('membershipTab.colMember')}</th>
                <th>{t('membershipTab.colBasis')}</th>
                <th>{t('membershipTab.colSince')}</th>
                <th>{t('membershipTab.colStatus')}</th>
              </tr>
            </thead>
            <tbody>
              {visibleRoster.map((r) => (
                <tr
                  key={`${r.userGuid}:${r.membershipType}`}
                  className={
                    selected &&
                    selected.userGuid === r.userGuid &&
                    selected.membershipType === r.membershipType
                      ? styles.rowSelected
                      : styles.row
                  }
                  onClick={() => openTrayFor(r)}
                >
                  <td>{r.name}</td>
                  <td className={styles.basis}>{r.membershipType}</td>
                  <td>{r.startedAt || '—'}</td>
                  <td>
                    <span className={`${styles.status} ${styles[`status_${r.status}`] || ''}`}>
                      {statusLabel(r.status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      {/* Modal — member detail (audience layer only; roles live on Team) */}
      {selected && !inviteOpen && (
        <div className={styles.modalOverlay} onClick={() => setSelected(null)}>
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
          <button type="button" className={styles.trayClose} onClick={() => setSelected(null)} aria-label={t('membershipTab.tray.close')}>
            ✕
          </button>
          <h3 className={styles.trayTitle}>{selected.name}</h3>
          <div className={styles.trayRow}>
            <span className={styles.trayKey}>{t('membershipTab.tray.email')}</span>
            <span>{emailByGuid.get(selected.userGuid.toUpperCase()) || '—'}</span>
          </div>
          <div className={styles.trayRow}>
            <span className={styles.trayKey}>{t('membershipTab.tray.membership')}</span>
            <span>{selected.membershipType}</span>
          </div>
          <div className={styles.trayRow}>
            <span className={styles.trayKey}>{t('membershipTab.tray.status')}</span>
            <span className={`${styles.status} ${styles[`status_${selected.status}`] || ''}`}>
              {statusLabel(selected.status)}
            </span>
          </div>
          <div className={styles.trayRow}>
            <span className={styles.trayKey}>{t('membershipTab.tray.since')}</span>
            <span>{selected.startedAt || '—'}</span>
          </div>
          <button
            type="button"
            className={styles.profileLink}
            onClick={() => openProfile(selected.userGuid)}
          >
            ↗ {t('membershipTab.tray.viewProfile')}
          </button>
          {isAdmin && (
            <div className={styles.trayActions}>
              {selected.status === 'active' && (
                <>
                  <button
                    type="button"
                    className={styles.btn}
                    disabled={busy}
                    onClick={() => setStatus(selected, 'suspended')}
                  >
                    {t('membershipTab.tray.suspend')}
                  </button>
                  <button
                    type="button"
                    className={styles.dangerBtn}
                    disabled={busy}
                    onClick={() => removeMember(selected)}
                  >
                    {t('membershipTab.tray.remove')}
                  </button>
                </>
              )}
              {selected.status === 'suspended' && (
                <>
                  <button
                    type="button"
                    className={styles.approveBtn}
                    disabled={busy}
                    onClick={() => setStatus(selected, 'active')}
                  >
                    {t('membershipTab.tray.reinstate')}
                  </button>
                  <button
                    type="button"
                    className={styles.dangerBtn}
                    disabled={busy}
                    onClick={() => removeMember(selected)}
                  >
                    {t('membershipTab.tray.remove')}
                  </button>
                </>
              )}
              {selected.status === 'invited' && (
                <button
                  type="button"
                  className={styles.dangerBtn}
                  disabled={busy}
                  onClick={() => withdrawInvitation(selected)}
                >
                  {t('membershipTab.tray.withdrawInvitation')}
                </button>
              )}
            </div>
          )}
          <div className={styles.trayNote}>{t('membershipTab.tray.rolesNote')}</div>
          </div>
        </div>
      )}

      {/* Modal — invite composer (Option A: one email box, auto-detect) */}
      {inviteOpen && (
        <div className={styles.modalOverlay} onClick={closeInvite}>
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
          <button type="button" className={styles.trayClose} onClick={closeInvite} aria-label={t('membershipTab.tray.close')}>
            ✕
          </button>
          <h3 className={styles.trayTitle}>{t('membershipTab.invite.title')}</h3>

          {inviteResult ? (
            /* ── Result panel — labels which path was taken ── */
            <div className={styles.inviteResult}>
              {inviteResult.kind === 'existing' ? (
                <>
                  <div className={styles.inviteBadgeExisting}>
                    {t('membershipTab.invite.resultExisting', { email: inviteResult.email })}
                  </div>
                  <div className={styles.trayNote}>
                    {t('membershipTab.invite.resultExistingNote')}
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.inviteBadgeNew}>
                    {t('membershipTab.invite.resultNew', { email: inviteResult.email })}
                  </div>
                  <label className={styles.trayLabel}>{t('membershipTab.invite.linkLabel')}</label>
                  <input
                    type="text"
                    className={styles.trayInput}
                    readOnly
                    value={inviteResult.link || ''}
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <button
                    type="button"
                    className={styles.primaryBtn}
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(inviteResult.link || '');
                        setLinkCopied(true);
                      } catch { /* clipboard blocked — user can select manually */ }
                    }}
                  >
                    {linkCopied ? t('membershipTab.invite.copied') : t('membershipTab.invite.copyLink')}
                  </button>
                  <div className={styles.trayNote}>{t('membershipTab.invite.resultNewNote')}</div>
                </>
              )}
              <div className={styles.trayActions}>
                <button
                  type="button"
                  className={styles.btn}
                  onClick={() => {
                    setInviteResult(null);
                    setInviteEmail('');
                    setInviteName('');
                    setInviteMessage('');
                    setLinkCopied(false);
                  }}
                >
                  {t('membershipTab.invite.another')}
                </button>
                <button type="button" className={styles.btn} onClick={closeInvite}>
                  {t('membershipTab.invite.done')}
                </button>
              </div>
            </div>
          ) : (
            /* ── Compose form ── */
            <>
              <label className={styles.trayLabel}>{t('membershipTab.invite.emailLabel')}</label>
              <input
                type="email"
                className={styles.trayInput}
                placeholder={t('membershipTab.invite.emailPlaceholder')}
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                autoComplete="off"
              />
              {inviteEmail.trim() && (
                <div className={styles.trayNote}>
                  {matchedExistingUser
                    ? t('membershipTab.invite.hintExisting')
                    : t('membershipTab.invite.hintNew')}
                </div>
              )}

              {/* Name only helps a NEW person (prefills their signup). */}
              {!matchedExistingUser && (
                <>
                  <label className={styles.trayLabel}>{t('membershipTab.invite.nameLabel')}</label>
                  <input
                    type="text"
                    className={styles.trayInput}
                    placeholder={t('membershipTab.invite.namePlaceholder')}
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                    autoComplete="off"
                  />
                </>
              )}

              <label className={styles.trayLabel}>{t('membershipTab.invite.message')}</label>
              <textarea
                className={styles.trayInput}
                rows={3}
                value={inviteMessage}
                onChange={(e) => setInviteMessage(e.target.value)}
              />
              <div className={styles.trayActions}>
                <button
                  type="button"
                  className={styles.primaryBtn}
                  disabled={busy || !inviteEmail.trim()}
                  onClick={handleGenerateInvite}
                >
                  {t('membershipTab.invite.generate')}
                </button>
                <button type="button" className={styles.btn} onClick={closeInvite}>
                  {t('membershipTab.invite.cancel')}
                </button>
              </div>
              <div className={styles.trayNote}>{t('membershipTab.invite.note')}</div>
            </>
          )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityMembershipTab;
